import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { subscribeToTransactionRules } from '@shared/services/transactionsService';
import { TransactionRule } from '@shared/types';
import { useLiveDataUid } from './useLiveDataUid';

/**
 * Focused slice behind the DataContext facade: the user's transaction rules
 * (userContent/{uid}/transactionRules). Rules are applied to transactions at
 * display-time by TransactionsProvider — never written back to Firestore.
 *
 * NOTE: components should keep consuming useData() from DataContext for now —
 * demo mode (DemoProviders) only stubs the facade's DataContext.Provider, so a
 * component reading this context directly would crash under /demo.
 */
interface TransactionRulesContextType {
    transactionRules: TransactionRule[];
}

export const TransactionRulesContext = createContext<TransactionRulesContextType | undefined>(undefined);

export const useTransactionRulesContext = () => {
    const context = useContext(TransactionRulesContext);
    if (!context) {
        throw new Error('useTransactionRulesContext must be used within a TransactionRulesProvider');
    }
    return context;
};

export const TransactionRulesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const uid = useLiveDataUid();
    const [transactionRules, setTransactionRules] = useState<TransactionRule[]>([]);

    useEffect(() => {
        if (!uid) {
            setTransactionRules([]);
            return;
        }
        return subscribeToTransactionRules(uid, setTransactionRules);
    }, [uid]);

    const value = useMemo(() => ({ transactionRules }), [transactionRules]);

    return (
        <TransactionRulesContext.Provider value={value}>
            {children}
        </TransactionRulesContext.Provider>
    );
};
